import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  IconChevronDown,
  IconChevronUp,
  IconEdit,
  IconTrash,
  IconRoute,
} from '@tabler/icons-react';
import type { SurveyPath, CardQuestion, SurveyQuestionType } from '../../types';
import { getPathColor } from '../../utils/pathColors';
import { normalizeTriggerValues } from '../../utils/pathTriggerValues';
import { QuestionBlock } from './QuestionBlock';
import { QuestionTypeBar } from './QuestionTypeBar';

interface PathSectionProps {
  path: SurveyPath;
  pathQuestions: CardQuestion[];
  triggerQuestion: CardQuestion;
  allQuestions: CardQuestion[];
  onQuestionUpdate: (questionId: string, updates: Partial<CardQuestion>) => void;
  onQuestionDelete: (questionId: string) => void;
  onQuestionDuplicate?: (questionId: string) => void;
  onPathEdit: () => void;
  onPathDelete: () => void;
  onAddQuestionToPath?: (type: SurveyQuestionType) => void;
  onDropQuestion?: (questionId: string) => void;
  onDropQuestionAtIndex?: (questionId: string, targetPathIndex: number) => void;
  onQuestionDragStart?: (e: React.DragEvent, questionId: string) => void;
  onQuestionDragEnd?: () => void;
  onReorderInPath?: (questionId: string, direction: 'up' | 'down') => void;
  defaultExpanded?: boolean;
}

export function PathSection({
  path,
  pathQuestions,
  triggerQuestion,
  allQuestions,
  onQuestionUpdate,
  onQuestionDelete,
  onQuestionDuplicate,
  onPathEdit,
  onPathDelete,
  onAddQuestionToPath,
  onDropQuestion,
  onDropQuestionAtIndex,
  onQuestionDragStart,
  onQuestionDragEnd,
  onReorderInPath,
  defaultExpanded = true,
}: PathSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [dragOver, setDragOver] = useState(false);
  const [dropTargetPathIndex, setDropTargetPathIndex] = useState<number | null>(null);
  const pathColor = getPathColor(path.id);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    setDropTargetPathIndex(null);
    try {
      const data = e.dataTransfer.getData('application/json');
      const payload = data ? JSON.parse(data) : null;
      if (payload?.type === 'question' && payload?.questionId && onDropQuestion) {
        onDropQuestion(payload.questionId);
      }
    } catch (err) {
      if (import.meta.env?.DEV) {
        console.warn('PathSection: invalid drop data (JSON parse failed)', err);
      }
    }
  };

  const handleDropAtIndex = (e: React.DragEvent, targetPathIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetPathIndex(null);
    try {
      const data = e.dataTransfer.getData('application/json');
      const payload = data ? JSON.parse(data) : null;
      if (payload?.type === 'question' && payload?.questionId && onDropQuestionAtIndex) {
        onDropQuestionAtIndex(payload.questionId, targetPathIndex);
      }
    } catch (err) {
      if (import.meta.env?.DEV) {
        console.warn('PathSection: invalid drop data (JSON parse failed)', err);
      }
    }
  };

  const handleDragOverAtIndex = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetPathIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);
  
  const triggerQuestionText = triggerQuestion?.text || 'Unknown question';
  const triggerValuesArr = normalizeTriggerValues(path.trigger.values);
  const triggerValuesDisplay =
    triggerValuesArr.length > 0
      ? triggerValuesArr.map((v) => `"${v}"`).join(', ')
      : '(no values selected)';
  const triggerLabel =
    triggerValuesArr.length > 1
      ? `is one of ${triggerValuesDisplay}`
      : `= ${triggerValuesDisplay}`;

  return (
    <Card
      className="my-2 overflow-hidden rounded-lg"
      style={{ border: `2px solid ${pathColor.main}` }}
    >
      {/* Path Header */}
      <div
        className="flex cursor-pointer items-center justify-between p-2 hover:opacity-90"
        style={{ backgroundColor: pathColor.light }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex flex-1 items-center gap-2">
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            style={{ color: pathColor.main }}
          >
            {expanded ? <IconChevronUp className="size-4" /> : <IconChevronDown className="size-4" />}
          </Button>

          <IconRoute className="size-5" style={{ color: pathColor.main }} />

          <div className="flex-1">
            <div className="mb-0.5 flex items-center gap-1">
              <Badge
                className="font-bold text-white"
                style={{ backgroundColor: pathColor.main }}
              >
                {path.name}
              </Badge>
              <Badge
                variant="outline"
                style={{ borderColor: pathColor.main, color: pathColor.main }}
              >
                {pathQuestions.length} {pathQuestions.length === 1 ? 'question' : 'questions'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Shows when: <strong>{triggerQuestionText}</strong> {triggerLabel}
            </p>
          </div>
        </div>

        <div className="flex gap-0.5">
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={(e) => {
              e.stopPropagation();
              onPathEdit();
            }}
            style={{ color: pathColor.main }}
          >
            <IconEdit className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={(e) => {
              e.stopPropagation();
              onPathDelete();
            }}
            className="text-destructive"
          >
            <IconTrash className="size-4" />
          </Button>
        </div>
      </div>

      {/* Path Questions (Collapsible) */}
      {expanded && (
        <div
          onClick={(e) => e.stopPropagation()}
          onDrop={onDropQuestion ? handleDrop : undefined}
          onDragOver={onDropQuestion ? handleDragOver : undefined}
          onDragLeave={onDropQuestion ? handleDragLeave : undefined}
          className={cn(
            'bg-background p-2',
            dragOver && onDropQuestion && 'rounded border-2 border-dashed',
          )}
          style={{
            borderTop: `1px solid ${pathColor.light}`,
            ...(dragOver && onDropQuestion ? { backgroundColor: pathColor.light, borderColor: pathColor.main } : {}),
          }}
        >
          {pathQuestions.length === 0 ? (
            <div className="py-2">
              <p className="mb-2 text-center text-sm text-muted-foreground">
                {onDropQuestion
                  ? 'Drop a question here to add it to this branch, or add a new question below.'
                  : 'No questions in this path. Add a question below or edit the path to add questions.'}
              </p>
              {onAddQuestionToPath && (
                <div className="flex justify-center">
                  <QuestionTypeBar onAddQuestion={onAddQuestionToPath} />
                </div>
              )}
            </div>
          ) : (
            <div
              className="pl-2"
              style={{ borderLeft: `3px solid ${pathColor.main}` }}
            >
              {pathQuestions.map((pathQuestion, pathIndex) => (
                <React.Fragment key={pathQuestion.id}>
                  {onDropQuestionAtIndex && (
                    <div
                      onDrop={(e) => handleDropAtIndex(e, pathIndex)}
                      onDragOver={(e) => handleDragOverAtIndex(e, pathIndex)}
                      onDragLeave={() => setDropTargetPathIndex(null)}
                      className="rounded transition-all duration-150"
                      style={{
                        minHeight: 8,
                        height: dropTargetPathIndex === pathIndex ? 14 : 8,
                        backgroundColor: dropTargetPathIndex === pathIndex ? pathColor.main : 'transparent',
                        opacity: dropTargetPathIndex === pathIndex ? 0.4 : 0,
                      }}
                    />
                  )}
                  <div className="mb-2">
                    <QuestionBlock
                      question={pathQuestion}
                      path={path}
                      onUpdate={(updates) => onQuestionUpdate(pathQuestion.id, updates)}
                      onDelete={() => onQuestionDelete(pathQuestion.id)}
                      onDuplicate={onQuestionDuplicate ? () => onQuestionDuplicate(pathQuestion.id) : undefined}
                      onEditPath={onPathEdit}
                      canMoveUp={pathIndex > 0}
                      canMoveDown={pathIndex < pathQuestions.length - 1}
                      onMoveUp={onReorderInPath && pathIndex > 0 ? () => onReorderInPath(pathQuestion.id, 'up') : undefined}
                      onMoveDown={onReorderInPath && pathIndex < pathQuestions.length - 1 ? () => onReorderInPath(pathQuestion.id, 'down') : undefined}
                      onDragStartForPath={onQuestionDragStart ? (e) => onQuestionDragStart(e, pathQuestion.id) : undefined}
                      onDragEndForPath={onQuestionDragEnd}
                    />
                  </div>
                </React.Fragment>
              ))}
              {onDropQuestionAtIndex && pathQuestions.length > 0 && (
                <div
                  onDrop={(e) => handleDropAtIndex(e, pathQuestions.length)}
                  onDragOver={(e) => handleDragOverAtIndex(e, pathQuestions.length)}
                  onDragLeave={() => setDropTargetPathIndex(null)}
                  className="rounded transition-all duration-150"
                  style={{
                    minHeight: 8,
                    height: dropTargetPathIndex === pathQuestions.length ? 14 : 8,
                    backgroundColor: dropTargetPathIndex === pathQuestions.length ? pathColor.main : 'transparent',
                    opacity: dropTargetPathIndex === pathQuestions.length ? 0.4 : 0,
                  }}
                />
              )}
              {onAddQuestionToPath && (
                <div className="mt-2">
                  <QuestionTypeBar onAddQuestion={onAddQuestionToPath} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
